import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Target, Sparkles, Shield, Globe, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

export const About = () => {
  const { t } = useTranslation();
  
  const values = [
    {
      icon: Heart,
      title: t('about.values.joy', 'Idrettsglede'),
      description: t('about.values.joyDesc', 'Alle idrettslige aktiviteter skal bygge på grunnverdier som idrettsglede, utvikling og fellesskap.')
    },
    {
      icon: Users,
      title: t('about.values.inclusion', 'Inkludering'),
      description: t('about.values.inclusionDesc', 'Skape en idrettskultur som inkluderer alle, på alle nivåer. Vi omfavner mangfold.')
    },
    {
      icon: Shield,
      title: t('about.values.respect', 'Respekt'),
      description: t('about.values.respectDesc', 'Møte diskriminering direkte med mot og konsistens. Arbeidet preges av demokrati, lojalitet og likeverd.')
    },
    {
      icon: Target,
      title: t('about.values.development', 'Utvikling'),
      description: t('about.values.developmentDesc', 'Utvikling er en reise, ikke et mål. Vi fokuserer på hele personen – mentalt, sosialt og fysisk.')
    },
  ];

  return (
    <div data-testid="about-page">
      <SEO 
        title={t('pg.About.c809e2')} 
        description={t('pg.About.5cdf0f')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80"
            alt={t('pg.About.4db8b0')}
            loading="eager"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('about.title', 'Om oss')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('pg.About.67dc29')}<br />
            <span className="text-green-400">{t('pg.About.b8868f')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {t('about.heroDescription', 'Mer enn sport – en plattform for personlig vekst, sosial integrering og samfunnsbygging.')}
          </p>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="py-12 bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <Calendar className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="text-3xl font-bold text-slate-900 dark:text-white">1955</p>
              <p className="text-slate-500">{t('about.founded', 'Grunnlagt')}</p>
            </div>
            <div>
              <MapPin className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{t('pg.About.5f457a')}</p>
              <p className="text-slate-500">{t('about.location', 'Bydel Bjerke, Oslo')}</p>
            </div>
            <div>
              <Sparkles className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="text-3xl font-bold text-slate-900 dark:text-white">5</p>
              <p className="text-slate-500">{t('about.sports', 'Idrettstilbud')}</p>
            </div>
            <div>
              <Users className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="text-3xl font-bold text-slate-900 dark:text-white">500+</p>
              <p className="text-slate-500">{t('about.members', 'Medlemmer')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg prose-invert mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_active-club-1/artifacts/h8quxnq7_VSK%20logo_1.png" 
                  alt={t('pg.About.ade82c')} 
                  className="h-16 w-auto"
                />
              </div>
              
              <p className="text-xl text-slate-600 leading-relaxed mb-6">
                {t('pg.About.58952a')}
              </p>

              <p className="text-slate-500 leading-relaxed mb-6">
                {t('pg.About.26b96b')}
              </p>

              <div className="bg-green-500/10 border-l-4 border-green-500 p-4 my-8">
                <p className="text-slate-600 italic">
                  {t('pg.About.f7f175')}
                </p>
              </div>

              <p className="text-slate-500 leading-relaxed mb-6">
                {t('pg.About.34f636')}
              </p>

              <p className="text-slate-500 leading-relaxed mb-6">
                {t('pg.About.c08699')}
              </p>

              <div className="border-t border-slate-200 pt-6 mt-8">
                <p className="text-green-500 font-medium">
                  {t('pg.About.b4ac04')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('about.ourValues', 'Våre verdier')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('about.whatWeStandFor', 'DET VI STÅR FOR')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 p-6 hover:border-green-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{value.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Activities */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('about.ourOfferings', 'Våre tilbud')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('about.activities', 'IDRETTSAKTIVITETER')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Link to="/soccer" className="group">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-green-500/50 transition-all duration-300">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80"
                    alt={t('pg.About.10066f')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-green-500 transition-colors">{t('pg.About.10066f')}</h3>
                  <p className="text-slate-500 text-sm">{t('pg.About.fea4ce')}</p>
                </div>
              </div>
            </Link>

            <Link to="/tennis" className="group">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-green-500/50 transition-all duration-300">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&q=80"
                    alt={t('pg.About.0c493d')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-green-500 transition-colors">{t('pg.About.0c493d')}</h3>
                  <p className="text-slate-500 text-sm">{t('pg.About.b41c9a')}</p>
                </div>
              </div>
            </Link>

            <Link to="/dance-pilates" className="group">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-green-500/50 transition-all duration-300">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=400&q=80"
                    alt={t('pg.About.e69fdb')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-green-500 transition-colors">{t('pg.About.e69fdb')}</h3>
                  <p className="text-slate-500 text-sm">{t('pg.About.3089db')}</p>
                </div>
              </div>
            </Link>

            <Link to="/dance-pilates" className="group">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-green-500/50 transition-all duration-300">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&q=80"
                    alt={t('pg.About.703de7')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-green-500 transition-colors">{t('pg.About.703de7')}</h3>
                  <p className="text-slate-500 text-sm">{t('pg.About.ec76c5')}</p>
                </div>
              </div>
            </Link>

            <Link to="/social-run-club" className="group">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-green-500/50 transition-all duration-300">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/5319373/pexels-photo-5319373.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=800"
                    alt={t('pg.About.736bff')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-green-500 transition-colors">{t('pg.About.736bff')}</h3>
                  <p className="text-slate-500 text-sm">{t('pg.About.614151')}</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold tracking-tighter text-slate-900 mb-4">
            {t('pg.About.c35fcd')}
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            {t('pg.About.c5c224')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-black font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-500/90">
                {t('pg.About.8df769')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-300 text-slate-900 uppercase tracking-wider px-8 py-6 hover:bg-slate-100 dark:bg-slate-700">
                {t('pg.About.8d1c6b')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default About;
